import * as React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';

/** Material UI */
import {
    Box,
    Button,
    ButtonGroup,
    ClickAwayListener,
    Grow,
    MenuItem,
    MenuList,
    Paper,
    Popper,
} from '@mui/material';
import { randomId } from '@mui/x-data-grid-generator';
import { GridCellModes, useGridApiRef } from '@mui/x-data-grid-premium';

/** Material UI Icons */
import AddIcon from '@mui/icons-material/Add';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import SearchIcon from '@mui/icons-material/Search';
import UploadIcon from '@mui/icons-material/Upload';

/** Redux Packages. */
import { useDispatch, useSelector } from 'react-redux';

/** Redux Reducers. */
import { setSnackbar } from 'shared/store/slices/snackBarSlice';

/** Internal Components */
import { callAPI } from 'shared/api/callApi';
import { MainDatagrid } from 'shared/ui/Datagrid';
import { CustomSkeleton2 } from 'shared/ui/Skeleton';
import ThemeButton, { DialogButton } from 'shared/ui/Button';

import {
    getColumns,
    DEFAULTS,
    isNegativeAmount,
    negativeValueMessage,
    collectTierMissingRows,
} from './DutyEntriesDef';
import BulkDutyEntryDialog from './BulkDutyEntryDialog';
import BulkErpResultDialog from './BulkErpResultDialog';
import DutyEntryDetail from './DutyEntryDetail';
import TierSearchDialog from './TierSearchDialog';
import { BULK_ERP_BLOCK_REASONS, MESSAGES, isJinroEntry, tierMissingUpdateBlockedMessage } from '../../lib/configUtils';
import { touchesSojuVolume } from '../../lib/sojuTier';
import { formatDateToDateString } from 'shared/lib/dateFormatter';
import { isSameCellValue } from 'shared/lib/datagridHelpers';

/** GET duty_entries: server pagination passes page / pageSize (JSON) in `condition` → { items, total }. */
const DUTY_ENTRIES_PAGE_SIZE_OPTIONS = [25, 50, 100];

/** Fields an operator can edit in the grid — only the ones that changed are sent on save. */
const EDITABLE_FIELDS = [
    'il_trade_partner',
    'import_date',
    'opus_reference',
    'entry_number',
    'contract_number',
    'entry_invoice_date',
    'entry_fee',
    'trucking_invoice_date',
    'trucking_fee',
    'reference',
];

const DATE_FIELDS = ['import_date', 'entry_invoice_date', 'trucking_invoice_date'];

/** Whether a grid row has an invoice date (pre-check before the Jinro POST). */
function dutyEntryHasInvoiceDate(value) {
    if (value == null) return false;
    if (value instanceof Date && !Number.isNaN(value.getTime())) return true;
    if (typeof value === 'string' && value.trim().length > 0) return true;
    return false;
}

/**
 * Whether an ERP update can be created from this row — at least one side (entry / trucking)
 * needs an invoice date together with a fee greater than zero. The server enforces the same
 * rule; checking here spares the operator a round trip. Single and bulk share this one rule.
 */
export function canCreateJinroSummary(row) {
    const sideOk = (dateValue, feeValue) => {
        const fee = Number(feeValue ?? 0);
        return dutyEntryHasInvoiceDate(dateValue) && Number.isFinite(fee) && fee > 0;
    };
    return sideOk(row?.entry_invoice_date, row?.entry_fee) || sideOk(row?.trucking_invoice_date, row?.trucking_fee);
}

/**
 * Why ERP cannot be updated for this entry, or null when it can.
 * 
 * @param {object} row - Duty entry as the grid holds it
 * @returns {string|null} - Reason to show the operator, or null when nothing blocks it
 */
export function jinroUpdateBlockReason(row) {
    if (!isJinroEntry(row)) return BULK_ERP_BLOCK_REASONS.NOT_JINRO;
    if (!canCreateJinroSummary(row)) return BULK_ERP_BLOCK_REASONS.MISSING_INVOICE;
    return null;
}

/** Displays and manages duty entries with inline editing and uploads. */
export default function DutyEntries({
    userEmail,
    getAccessTokenSilently,
    loadingState,
    setLoadingState,
    itemFormulasRows,
}) {
    /** Dispatches snackbar notifications for user feedback. */
    const dispatch = useDispatch();

    /** Client list for the autocomplete. */
    const clientList = useSelector((state) => state.clientList);

    /** Duty entries for the grid (Current page rows only - Server pagination). */
    const [dutyEntries, setDutyEntries] = useState([]);

    /** Grid command handle used to open a cell for editing on click (single-click editing). */
    const apiRef = useGridApiRef();

    /** Open state of the Bulk Create dialog, where many entries are registered at once. */
    const [bulkDialogOpen, setBulkDialogOpen] = useState(false);

    /** Open state of the TIER Search dialog, where the quarterly soju accumulation is looked up. */
    const [tierSearchOpen, setTierSearchOpen] = useState(false);

    /** Entries the operator checked for a bulk ERP update. */
    const [rowSelectionModel, setRowSelectionModel] = useState([]);

    /** Result of the last bulk ERP update — shown until the operator closes it. */
    const [bulkErpResult, setBulkErpResult] = useState(null);

    /** Dropdown attached to the NEW button (split button) — same pattern as shipment's NEW button. */
    const [newMenuOpen, setNewMenuOpen] = useState(false);
    const newMenuAnchorRef = useRef(null);

    /** Server Pagination */
    const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 100 });
    const [rowCount, setRowCount] = useState(0);
    const [gridLoading, setGridLoading] = useState(false);
    const [listVersion, setListVersion] = useState(0);
    const initialFetchDone = useRef(false);
    const [filterModel, setFilterModel] = useState({ items: [], quickFilterValues: [] });
    const [debouncedQuickFilter, setDebouncedQuickFilter] = useState('');
    const lastDebouncedQuickFilterRef = useRef('');

    useEffect(() => {
        const q = (filterModel.quickFilterValues ?? []).join(' ').trim();
        const t = window.setTimeout(() => {
            if (lastDebouncedQuickFilterRef.current === q) return;
            lastDebouncedQuickFilterRef.current = q;
            setDebouncedQuickFilter(q);
            setPaginationModel((p) => ({ ...p, page: 0 }));
            setRowSelectionModel([]);
        }, 100);
        return () => window.clearTimeout(t);
    }, [filterModel]);

    const handleAddClick = () => {
        const newRow = {
            id: randomId(),
            ...DEFAULTS,
        };

        callAPI({
            method: 'post',
            module: 'duty_calculator',
            component: 'duty_entry',
            getAccessTokenSilently,
            email: userEmail,
            obj: newRow,
            onInit: () => setGridLoading(true),
            onFinal: () => setGridLoading(false),
            onSuccess: (resp) => {
                console.log('add duty entry response', resp);
                const created = resp?.data ?? resp;
                const hasActiveQuickFilter = Boolean(debouncedQuickFilter);
                const isFirstPage = paginationModel.page === 0;

                if (created && created.id != null) {
                    if (!hasActiveQuickFilter && isFirstPage) {
                        const normalizedCreated = {
                            ...created,
                            il_duty_entry_detail: created.il_duty_entry_detail || [],
                        };
                        setDutyEntries((prev) => [normalizedCreated, ...prev].slice(0, paginationModel.pageSize));
                        setRowCount((count) => count + 1);
                    } else {
                        setPaginationModel((prev) => ({ ...prev, page: 0 }));
                        setListVersion((v) => v + 1);
                    }
                }
                dispatch(setSnackbar({ open: true, message: MESSAGES.ADD_SUCCESS, severity: 'success' }));
            },
            onError: (error) => {
                dispatch(
                    setSnackbar({
                        open: true,
                        message: error?.message ?? 'Failed to add duty entry.',
                        severity: 'error',
                    }),
                );
            },
        });
    };

    /** Handle to toggle the "NEW" split button's dropdown menu. */
    const handleNewMenuToggle = () => {
        setNewMenuOpen((prevOpen) => !prevOpen);
    };

    /** Handle to close the "NEW" split button's dropdown menu. */
    const handleNewMenuClose = (event) => {
        if (newMenuAnchorRef.current && newMenuAnchorRef.current.contains(event.target)) {
            return;
        }
        setNewMenuOpen(false);
    };

    /** Handle to open the bulk create dialog from the dropdown menu. */
    const handleBulkCreateClick = () => {
        setNewMenuOpen(false);
        setBulkDialogOpen(true);
    };

    /** A single click on a cell switches it straight into edit mode. */
    const handleCellClick = useCallback(
        (params, event) => {
            // Read-only cells
            if (!params.isEditable) return;
            // Clipboard-driven cell: a single click only selects (edit via double click / Enter)
            if (params.field === 'il_trade_partner') return;
            // Already being edited — reopening would reset what's being typed
            if (apiRef.current?.getCellMode?.(params.id, params.field) === GridCellModes.Edit) return;
            // Ctrl/Shift clicks are copy-selection gestures, so don't open the editor (cellSelection)
            if (event.ctrlKey || event.metaKey || event.shiftKey) return;

            apiRef.current?.startCellEditMode?.({ id: params.id, field: params.field });
        },
        [apiRef],
    );

    const handleDeleteClick = (id) => {
        const isConfirmed = window.confirm('Delete this duty entry? This action cannot be undone.');
        if (!isConfirmed) return;

        callAPI({
            method: 'delete',
            module: 'duty_calculator',
            component: 'duty_entry',
            getAccessTokenSilently,
            email: userEmail,
            obj: { id: id },
            onInit: () => setGridLoading(true),
            onFinal: () => setGridLoading(false),
            onSuccess: (resp) => {
                console.log('delete duty entry response', resp);
                setDutyEntries((prev) => {
                    const next = prev.filter((e) => e.id !== id);
                    if (next.length === 0) {
                        setPaginationModel((p) => (p.page > 0 ? { ...p, page: p.page - 1 } : p));
                    }
                    return next;
                });
                setRowCount((c) => Math.max(0, c - 1));
                setRowSelectionModel((prev) => prev.filter((selectedId) => selectedId !== id));
                dispatch(setSnackbar({ open: true, message: MESSAGES.DELETE_SUCCESS, severity: 'success' }));
            },
            onError: (error) => {
                dispatch(setSnackbar({ open: true, message: error.message, severity: 'error' }));
            },
        });
    };

    const handleUpdateErpClick = (row) => {
        // Tier 필수 품목의 행에 Tier가 비어 있으면 그 행의 CBMA가 0으로 잡혀 있다 — 담당자에게
        // 어느 품목인지 알리고 요청을 보내지 않는다. 서버도 같은 행을 보고 400으로 막는다.
        const tierMissingRows = collectTierMissingRows(row?.il_duty_entry_detail);
        if (tierMissingRows.length > 0) {
            window.alert(tierMissingUpdateBlockedMessage(tierMissingRows));
            return;
        }

        const isConfirmed = window.confirm('Do you want to update ERP?');
        if (!isConfirmed) return;

        const blockReason = jinroUpdateBlockReason(row);
        if (blockReason) {
            dispatch(
                setSnackbar({
                    open: true,
                    message: `Cannot update ERP: ${blockReason}`,
                    severity: 'error',
                    autoHideDuration: 3000,
                }),
            );
            return;
        }

        callAPI({
            method: 'post',
            module: 'duty_calculator',
            component: 'jinro_summary',
            getAccessTokenSilently,
            email: userEmail,
            obj: { il_duty_entry_id: row.id },
            onInit: () => setGridLoading(true),
            onFinal: () => setGridLoading(false),
            onSuccess: (resp) => {
                console.log('update jinro summaries response', resp);
                dispatch(setSnackbar({ open: true, message: MESSAGES.UPDATE_SUCCESS, severity: 'success' }));
            },
            onError: (error) => {
                console.log('update jinro summaries error', error);
                dispatch(setSnackbar({ open: true, message: error.message, severity: 'error' }));
            },
        });
    };

    /**
     * Updates ERP for every checked entry in one request.
     *
     * The batch is not all-or-nothing: an entry the server cannot take is skipped and the rest are
     * still updated, so a hundred-entry selection never has to be redone over one missing invoice
     * date. Every checked entry is sent and judged by the server — one rule, in one place — and
     * whatever it refused stays checked and is named in the result window, ready to be fixed and
     * sent again.
     */
    const handleBulkUpdateErpClick = () => {
        const selectedIds = rowSelectionModel;
        if (selectedIds.length === 0) return;

        const isConfirmed = window.confirm(
            `Update ERP for ${selectedIds.length} selected ${selectedIds.length === 1 ? 'entry' : 'entries'}? ` +
                'Entries that cannot be updated stay selected and are listed when it finishes.',
        );
        if (!isConfirmed) return;

        callAPI({
            method: 'post',
            module: 'duty_calculator',
            component: 'jinro_summary',
            type: 'multiple',
            getAccessTokenSilently,
            email: userEmail,
            obj: selectedIds.map((id) => ({ il_duty_entry_id: id })),
            onInit: () => setGridLoading(true),
            onFinal: () => setGridLoading(false),
            onSuccess: (resp) => {
                console.log('bulk update jinro summaries response', resp);
                const summary = resp?.data ?? {};
                // 서버는 막힌 건의 id와 사유만 돌려준다 — 담당자가 읽는 이름은 고른 행이 갖고 있다.
                const failed = (summary.failed ?? []).map((failure) => {
                    const row = dutyEntries.find((entry) => entry.id === failure.il_duty_entry_id);
                    return { ...failure, opus_reference: row?.opus_reference, entry_number: row?.entry_number };
                });
                setRowSelectionModel(failed.map((failure) => failure.il_duty_entry_id));
                setBulkErpResult({ ...summary, failed });
            },
            onError: (error) => {
                console.log('bulk update jinro summaries error', error);
                // 요청 자체가 닿지 못했으므로 아무것도 반영되지 않았다 — 선택은 그대로 둔다.
                dispatch(
                    setSnackbar({
                        open: true,
                        message: error.message,
                        severity: 'error',
                        autoHideDuration: 6000,
                    }),
                );
            },
        });
    };

    /** The detail row this entry holds under that id, as it stands before the change is applied. */
    const currentDetailOf = (entryId, detailId) =>
        dutyEntries.find((row) => row.id === entryId)?.il_duty_entry_detail?.find((d) => d.id === detailId);

    /**
     * Keeps the YTD Soju PFG column true after the operator changes soju volume.
     *
     * The year-to-date figure on each entry depends on every other entry of that year, so after
     * a soju row is added, edited or deleted the operator would otherwise pick the next tier from
     * a total that no longer includes their own change — and the pre-save check would not warn,
     * because changing a quantity never touches the Tier cell. The list is re-read quietly: no
     * skeleton, and the page, the filter and any open detail panel stay where they are.
     */
    const refreshListIfSojuChanged = (...rows) => {
        if (touchesSojuVolume(...rows)) setListVersion((v) => v + 1);
    };

    /**
     * Saves what the operator changed on an entry row — one cell, or several at once from a paste.
     *
     * Only the changed cells are sent, so display-only values that came with the read cannot get
     * the save rejected. Fees cannot go negative. Moving the import date re-sorts the year's soju
     * accumulation, so the list is re-read for the other entries of that year to catch up.
     */
    const handleProcessRowUpdate = async (newRow, oldRow) => {
        const changedFields = EDITABLE_FIELDS.filter((field) => !isSameCellValue(newRow[field], oldRow[field]));

        // If no changes, don't call server and return oldRow
        if (changedFields.length === 0) {
            return oldRow;
        }

        // A paste skips the cell-edit validation, so negative fees are caught here
        const negativeFeeField = ['entry_fee', 'trucking_fee'].find(
            (field) => changedFields.includes(field) && isNegativeAmount(newRow[field]),
        );
        if (negativeFeeField) {
            const label = negativeFeeField === 'entry_fee' ? 'Entry Fee' : 'Trucking Fee';
            dispatch(setSnackbar({ open: true, message: negativeValueMessage(label), severity: 'error' }));
            return oldRow;
        }

        const payload = { id: newRow.id };
        changedFields.forEach((field) => {
            const value = newRow[field];
            if (field === 'il_trade_partner') {
                payload[field] =
                    value && typeof value === 'object' ? { id: value.id, name: value.name ?? '' } : value;
            } else if (DATE_FIELDS.includes(field)) {
                payload[field] = value instanceof Date ? formatDateToDateString(value) : value;
            } else {
                payload[field] = value;
            }
        });

        try {
            const resp = await new Promise((resolve, reject) => {
                callAPI({
                    method: 'put',
                    module: 'duty_calculator',
                    component: 'duty_entry',
                    getAccessTokenSilently,
                    email: userEmail,
                    obj: payload,
                    onInit: () => setGridLoading(true),
                    onFinal: () => setGridLoading(false),
                    onSuccess: (resp) => {
                        console.log('update duty entry response', resp);
                        dispatch(
                            setSnackbar({
                                open: true,
                                message: MESSAGES.UPDATE_SUCCESS,
                                severity: 'success',
                            }),
                        );
                        resolve(resp);
                    },
                    onError: (error) => {
                        dispatch(
                            setSnackbar({
                                open: true,
                                message: error.message,
                                severity: 'error',
                            }),
                        );
                        reject(error);
                    },
                });
            });

            const updatedEntry = resp.data;
            setDutyEntries((prev) => prev.map((row) => (row.id === newRow.id ? updatedEntry : row)));

            // An entry with no soju has no place in the year's running order, so only a soju entry
            // moving its date changes the other entries' YTD figures.
            const entryHoldsSoju = updatedEntry?.soju_pfg != null || oldRow?.soju_pfg != null;
            if (changedFields.includes('import_date') && entryHoldsSoju) {
                setListVersion((v) => v + 1);
            }
            return newRow;
        } catch (error) {
            throw error;
        }
    };

    /** Sync duty entry details with parent state so panel reopen shows correct data. */
    const handleEntryDetailsAdded = (entryId, newDetails) => {
        const list = Array.isArray(newDetails) ? newDetails : [newDetails];
        setDutyEntries((prev) =>
            prev.map((row) =>
                row.id === entryId
                    ? { ...row, il_duty_entry_detail: [...(row.il_duty_entry_detail || []), ...list] }
                    : row,
            ),
        );
        refreshListIfSojuChanged(list);
    };
    const handleEntryDetailsUpdated = (entryId, updatedDetail) => {
        // The row as it was counts too — a soju row turned into another product still left the year.
        const previousDetail = currentDetailOf(entryId, updatedDetail.id);
        setDutyEntries((prev) =>
            prev.map((row) =>
                row.id === entryId
                    ? {
                          ...row,
                          il_duty_entry_detail: (row.il_duty_entry_detail || []).map((d) =>
                              d.id === updatedDetail.id ? updatedDetail : d,
                          ),
                      }
                    : row,
            ),
        );
        refreshListIfSojuChanged(previousDetail, updatedDetail);
    };
    const handleEntryDetailsDeleted = (entryId, detailId) => {
        // Only the id arrives, so the row is looked up before it leaves the list.
        const deletedDetail = currentDetailOf(entryId, detailId);
        setDutyEntries((prev) =>
            prev.map((row) =>
                row.id === entryId
                    ? {
                          ...row,
                          il_duty_entry_detail: (row.il_duty_entry_detail || []).filter((d) => d.id !== detailId),
                      }
                    : row,
            ),
        );
        refreshListIfSojuChanged(deletedDetail);
    };

    const loadDutyEntries = useCallback(() => {
        if (!userEmail) return;

        callAPI({
            method: 'get',
            module: 'duty_calculator',
            component: 'duty_entries',
            getAccessTokenSilently,
            email: userEmail,
            condition: JSON.stringify({
                page: paginationModel.page,
                pageSize: paginationModel.pageSize,
                ...(debouncedQuickFilter ? { quickFilter: debouncedQuickFilter } : {}),
            }),
            onInit: () => {
                // Only top skeleton on first load. POST after listVersion GET·page change GET doesn't use gridLoading (to avoid double spinner)
                if (!initialFetchDone.current) {
                    setLoadingState((prev) => ({ ...prev, dutyEntries: true }));
                }
            },
            onSuccess: (resp) => {
                console.log('duty entries', resp);
                const data = resp?.data;
                if (data && typeof data === 'object' && !Array.isArray(data) && Array.isArray(data.items)) {
                    setDutyEntries(data.items);
                    setRowCount(typeof data.total === 'number' ? data.total : 0);
                } else if (Array.isArray(data)) {
                    // Legacy: array of all (when called without condition, etc.)
                    setDutyEntries(data);
                    setRowCount(data.length);
                } else {
                    setDutyEntries([]);
                    setRowCount(0);
                }
                if (!initialFetchDone.current) {
                    setLoadingState((prev) => ({ ...prev, dutyEntries: false }));
                    initialFetchDone.current = true;
                }
            },
            onError: (error) => {
                dispatch(setSnackbar({ open: true, message: error.message, severity: 'error' }));
                if (!initialFetchDone.current) {
                    setLoadingState((prev) => ({ ...prev, dutyEntries: false }));
                    initialFetchDone.current = true;
                }
            },
        });
    }, [
        debouncedQuickFilter,
        dispatch,
        getAccessTokenSilently,
        paginationModel.page,
        paginationModel.pageSize,
        setLoadingState,
        userEmail,
    ]);

    useEffect(() => {
        if (!userEmail) return;
        loadDutyEntries();
    }, [userEmail, loadDutyEntries, listVersion]);

    if (loadingState.dutyEntries) {
        return (
            <Box sx={{ width: '100%', height: '100%', p: 2 }}>
                <CustomSkeleton2 />
            </Box>
        );
    }

    return (
        <Box sx={{ p: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 1, mb: 1 }}>
                <DialogButton
                    text="TIER SEARCH"
                    icon={<SearchIcon />}
                    onClick={() => setTierSearchOpen(true)}
                    sx={{ width: '128px', height: '30px', border: 'solid 1px' }}
                />
                <ThemeButton
                    text={
                        rowSelectionModel.length > 0 ? `UPDATE ERP (${rowSelectionModel.length})` : 'UPDATE ERP'
                    }
                    icon={<UploadIcon />}
                    sx={{ height: '30px', px: 1.5, gap: 0.5, whiteSpace: 'nowrap' }}
                    onClick={handleBulkUpdateErpClick}
                    disabled={rowSelectionModel.length === 0}
                />
                <ButtonGroup variant="contained" ref={newMenuAnchorRef} sx={{ height: '30px', boxShadow: 'none' }}>
                    <ThemeButton
                        text="NEW"
                        icon={<AddIcon />}
                        sx={{
                            width: '68px',
                            height: '30px',
                            borderTopRightRadius: 0,
                            borderBottomRightRadius: 0,
                        }}
                        onClick={handleAddClick}
                    />
                    <Button
                        size="small"
                        aria-controls={newMenuOpen ? 'new-duty-entry-split-button-menu' : undefined}
                        aria-expanded={newMenuOpen ? 'true' : undefined}
                        aria-label="select new duty entry mode"
                        aria-haspopup="menu"
                        onClick={handleNewMenuToggle}
                        sx={{
                            color: 'white',
                            bgcolor: '#00647e',
                            minWidth: '22px',
                            width: '22px',
                            height: '30px',
                            borderTopLeftRadius: 0,
                            borderBottomLeftRadius: 0,
                            borderLeft: '1px solid rgba(255,255,255,0.4)',
                        }}
                    >
                        <ArrowDropDownIcon sx={{ fontSize: '18px' }} />
                    </Button>
                </ButtonGroup>
                <Popper
                    open={newMenuOpen}
                    anchorEl={newMenuAnchorRef.current}
                    role={undefined}
                    transition
                    disablePortal
                    placement="bottom-end"
                    sx={{ zIndex: 1300 }}
                >
                    {({ TransitionProps, placement }) => (
                        <Grow
                            {...TransitionProps}
                            style={{
                                transformOrigin: placement === 'bottom-end' ? 'right top' : 'right bottom',
                            }}
                        >
                            <Paper>
                                <ClickAwayListener onClickAway={handleNewMenuClose}>
                                    <MenuList id="new-duty-entry-split-button-menu" autoFocusItem={newMenuOpen}>
                                        <MenuItem onClick={handleBulkCreateClick}>Bulk Create</MenuItem>
                                    </MenuList>
                                </ClickAwayListener>
                            </Paper>
                        </Grow>
                    )}
                </Popper>
            </Box>
            <TierSearchDialog
                open={tierSearchOpen}
                onClose={() => setTierSearchOpen(false)}
                userEmail={userEmail}
                getAccessTokenSilently={getAccessTokenSilently}
            />
            <BulkDutyEntryDialog
                open={bulkDialogOpen}
                onClose={() => setBulkDialogOpen(false)}
                clientList={clientList}
                userEmail={userEmail}
                getAccessTokenSilently={getAccessTokenSilently}
                onCreated={() => {
                    setPaginationModel((prev) => ({ ...prev, page: 0 }));
                    setListVersion((v) => v + 1);
                }}
            />
            <BulkErpResultDialog
                open={bulkErpResult != null}
                result={bulkErpResult}
                onClose={() => setBulkErpResult(null)}
            />
            <MainDatagrid
                apiRef={apiRef}
                loading={gridLoading}
                rows={dutyEntries}
                cols={getColumns({
                    handleDeleteClick,
                    handleUpdateErpClick,
                    clientList,
                })}
                showToolbar="GridToolbar"
                id_status={false}
                /** Checkboxes drive the bulk ERP update — cell editing/copying stays unchanged. */
                checkboxSelection
                rowSelectionModel={rowSelectionModel}
                setRowSelectionModel={setRowSelectionModel}
                additional={{
                    /** ERP holds JINRO entries only, so no other client's entry can be checked. */
                    isRowSelectable: ({ row }) => isJinroEntry(row),
                    rowHeight: 40,
                    editMode: 'cell',
                    disableDensitySelector: true,
                    disableAggregation: true,
                    cellSelection: true,
                    disableRowSelectionOnClick: true,
                    /** The server sends only one page, so the client must not paginate the rows itself */
                    paginationMode: 'server',
                    filterMode: 'server',
                    filterModel,
                    onFilterModelChange: (model) => setFilterModel(model),
                    rowCount,
                    paginationModel,
                    onPaginationModelChange: (model) => {
                        setRowSelectionModel([]);
                        setPaginationModel(model);
                    },
                    pageSizeOptions: DUTY_ENTRIES_PAGE_SIZE_OPTIONS,
                    getDetailPanelContent: ({ row }) => (
                        <DutyEntryDetail
                            userEmail={userEmail}
                            getAccessTokenSilently={getAccessTokenSilently}
                            dutyEntry={row}
                            itemFormulasRows={itemFormulasRows}
                            setGridLoading={setGridLoading}
                            onEntryDetailsAdded={handleEntryDetailsAdded}
                            onEntryDetailsUpdated={handleEntryDetailsUpdated}
                            onEntryDetailsDeleted={handleEntryDetailsDeleted}
                        />
                    ),
                    getDetailPanelHeight: () => 'auto',
                    slotProps: {
                        toolbar: {
                            showQuickFilter: true,
                            printOptions: { disableToolbarButton: true },
                            excelOptions: { disableToolbarButton: true },
                            csvOptions: { disableToolbarButton: true },
                        },
                    },
                    processRowUpdate: handleProcessRowUpdate,
                    onCellClick: handleCellClick,
                }}
                sx={{
                    height: '80vh',
                    textAlign: 'center',
                    '& .custom-editable-header': {
                        textDecoration: 'underline', // editable mark
                    },
                    '& .MuiDataGrid-columnSeparator': { display: 'none' },
                    '& .MuiDataGrid-iconSeparator': { display: 'none' },
                }}
            />
        </Box>
    );
}
